import { computed, ref, watch } from "vue";

import { fetchSignedFileUrlOrThrow } from "@/api/fileSignedUrl";
import { useFileOpen } from "@/composables/useFileOpen";
import { useLayoutStore } from "@/store/layoutStore";

interface ShownFile {
  url: string;
  type: string;
  name: string;
}

/**
 * The preview bar's file and signed URL, fetched each time the bar is shown or its file changes, so
 * it never holds an expired one; and opening the bar's file in the overlay. Switching files keeps the
 * current one shown until the next one's URL arrives, so the bar never blanks in between; a file
 * that goes away clears it at once. A failure is reported in `errorMessage` for the bar to show in place.
 */
export function usePreviewBarFile() {
  const layoutStore = useLayoutStore();
  const { openPreviewOverlay } = useFileOpen();
  const shownFile = ref<ShownFile>();
  const errorMessage = ref("");
  let isExpanding = false;

  watch(
    () =>
      [
        layoutStore.bars.previewBar.visible,
        layoutStore.previewFile.objectName,
        layoutStore.previewFile.generation,
      ] as const,
    async ([visible, objectName, generation], _, onCleanup) => {
      errorMessage.value = "";
      if (!visible || !objectName) {
        shownFile.value = undefined;
        return;
      }
      let stale = false;
      onCleanup(() => (stale = true));
      const { type, name } = layoutStore.previewFile;
      try {
        const url = await fetchSignedFileUrlOrThrow(objectName, generation);
        if (!stale) shownFile.value = { url, type, name };
      } catch (error) {
        if (stale) return;
        shownFile.value = undefined;
        errorMessage.value = error instanceof Error ? error.message : String(error);
      }
    },
    { immediate: true },
  );

  const fileUrl = computed(() => shownFile.value?.url ?? "");
  const fileType = computed(() => shownFile.value?.type);
  const fileName = computed(() => shownFile.value?.name);
  const loading = computed(
    () =>
      !shownFile.value &&
      !errorMessage.value &&
      (!!layoutStore.previewFile.objectName || layoutStore.previewFilePending),
  );

  async function expandPreview() {
    if (isExpanding) return;
    isExpanding = true;
    try {
      await openPreviewOverlay(layoutStore.previewFile);
    } finally {
      isExpanding = false;
    }
  }

  return { fileUrl, fileType, fileName, loading, errorMessage, expandPreview };
}
